import { useState, useEffect } from 'react';
import * as vp from 'vopay';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SubAccountTransferForm } from './types';
import { useClientData } from '@/providers/ClientData.provider';
import { buildTransferFundRows } from './buildTransferFundRows.helper';
import styles from './TransferFunds.module.scss';
import { useForm } from '../../Form/utils';
import useFetchDefaultBankAccount from '@/hooks/useApi/useFetchDefaultBankAccount';
import { useDialog } from '@/providers/Dialog.provider';
import NoOperationalDefault from '../NoOperationalDefault/NoOperationalDefault';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

export type TransferFundsFormProps = {
  defaultValues: SubAccountTransferForm;
  onSubmit: (values: SubAccountTransferForm) => void;
};
const classes = vp.withStyles(styles);
const { Box, Button, Card, Icon } = vp;

const TransferFundsForm: React.FC<TransferFundsFormProps> = ({ defaultValues, onSubmit }) => {
  const { userSettings } = useUserSettingsProvider();
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.TransferFunds.TransferFundsForm' });
  const [defaultBankAccountResponse, getDefaultBankAccount] = useFetchDefaultBankAccount({});
  const dialogue = useDialog();
  const [defaultBank, setDefaultBank] = useState<vp.DefaultBankAccountList>();
  useEffect(() => {
    getDefaultBankAccount({
      Timezone: userSettings.timezone,
    });
  }, []);
  const history = useHistory();
  const { response } = useClientData();
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 1);

  const [isUnderReview, setIsUnderReview] = useState(false);

  const [showingMore, setShowingMore] = useState(false);

  const fieldDataList = buildTransferFundRows({
    defaultBank,
    clientData: response,
    showingMore,
    parentAccount: {
      AccountID: response.ParentAccountID,
      AccountName: response.ParentAccountName,
    },
  });

  const { renderFormElements, ctx } = useForm<SubAccountTransferForm, SubAccountTransferForm>({
    config: fieldDataList,
    initialValues: defaultValues,
    isReviewing: isUnderReview,
    onSubmit: (serializedData) => {
      onSubmit(serializedData);
    },
  });

  useEffect(() => {
    if (defaultBankAccountResponse.Success) {
      const defaultOperationalAccount = defaultBankAccountResponse.DefaultOperationalBankAccount.filter(
        (bankAccount) => bankAccount.Currency === ctx.values.Currency,
      ).pop();
      if (defaultOperationalAccount) {
        ctx.setFieldValue('DefaultOperationalBankAccount', defaultOperationalAccount.Token);
        setDefaultBank(defaultOperationalAccount);
      } else {
        const openDialog = async () => {
          const confirmation = await dialogue.open(NoOperationalDefault, { bare: true });
          if (confirmation) {
            history.push('/operational-accounts');
          }
        };
        openDialog();
      }
    }
  }, [defaultBankAccountResponse, ctx.values.Currency]);

  return (
    <div style={{ width: '100%' }}>
      <form onSubmit={ctx.handleSubmit}>
        <Box flx="col" base="8" gap="lg">
          <Card>
            <Box gap="lg" base="8" flx="col">
              <div className={classes('grid')}>{renderFormElements({ config: fieldDataList })}</div>
              {!showingMore && (
                <Box jc="fs">
                  <Button
                    color="none"
                    background="transparent"
                    textColor="#2F82FF"
                    noPadding
                    noShadow
                    label={
                      <Box flx="row" gapSize="4px" jc="center" ai="center">
                        <Icon height="20px" width="20px" icon="add" color="blue" bgColor="none" />
                        <span>{t('moreInfo')}</span>
                      </Box>
                    }
                    onClick={() => {
                      setShowingMore(true);
                    }}
                  />
                </Box>
              )}
            </Box>
          </Card>
        </Box>

        <Box jc="fe" gap="sm" mt="lg">
          {isUnderReview ? (
            <Button
              id="Fund-back"
              height="32px"
              color="secondary"
              type="button"
              label={t('buttons.back')}
              onClick={() => {
                setIsUnderReview(false);
              }}
            />
          ) : null}
          {!isUnderReview && (
            <Button
              id="Fund-review"
              height="32px"
              isDisabled={!ctx.isValid || Object.values(ctx.errors).length > 0}
              type="button"
              label={t('buttons.review')}
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setIsUnderReview(true);
              }}
            />
          )}
          {isUnderReview && (
            <Button
              id="Fund-submit"
              isDisabled={!ctx.isValid || Object.values(ctx.errors).length > 0}
              type="submit"
              height="32px"
              label={t('buttons.submit')}
            />
          )}
        </Box>
      </form>
    </div>
  );
};

export default TransferFundsForm;
